import type { Dispatch, SetStateAction } from 'react';
import {
  Checkbox,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import { All } from './FilterColumn';

export interface FilterListItemProps<FilterOptionType> {
  filterItem: { label: string; filterExpr: { value: FilterOptionType } };
  selectedFilters: Array<{
    label: string;
    filterExpr: { value: FilterOptionType };
  }>;
  setSelectedFilters: Dispatch<
    SetStateAction<
      Array<{ label: string; filterExpr: { value: FilterOptionType } }>
    >
  >;
  allFilterValues: Array<{
    label: string;
    filterExpr: { value: FilterOptionType };
  }>;
}

const FilterListItem = <FilterOptionType extends string>({
  filterItem,
  selectedFilters,
  setSelectedFilters,
  allFilterValues,
}: FilterListItemProps<FilterOptionType>) => {
  const {
    label,
    filterExpr: { value },
  } = filterItem;
  const indeterminate =
    value === All &&
    selectedFilters
      .map(({ filterExpr }) => filterExpr.value)
      .includes(All as FilterOptionType) &&
    selectedFilters.length < allFilterValues.length;

  const handleClick = () =>
    setSelectedFilters((prev) => {
      if (value === All) {
        if (
          indeterminate ||
          !selectedFilters
            .map(({ filterExpr }) => filterExpr.value)
            .includes(value)
        ) {
          return allFilterValues;
        }
        return [];
      }
      if (prev.map(({ filterExpr }) => filterExpr.value).includes(value)) {
        return prev.filter(
          (groupFilter) => groupFilter.filterExpr.value !== value,
        );
      }
      return [...prev, filterItem];
    });
  return (
    <ListItemButton
      role="listitem"
      onClick={handleClick}
      sx={(theme) => ({ height: theme.spacing(4.5) })}
    >
      <ListItemIcon>
        <Checkbox
          indeterminate={indeterminate}
          checked={selectedFilters
            .map(({ filterExpr }) => filterExpr.value)
            .includes(value)}
          tabIndex={-1}
          disableRipple
          inputProps={{
            'aria-labelledby': `${value}-label`,
          }}
          size="small"
        />
      </ListItemIcon>
      <ListItemText
        id={`${value}-label`}
        primary={<Typography variant="body4">{label}</Typography>}
      />
    </ListItemButton>
  );
};

export default FilterListItem;
